import React, { useState, useEffect } from "react";
import { useIsMobile } from "../../hooks/useIsMobile";

/**
 * Wraps application content and shows a full-screen overlay
 * when device is in landscape orientation, prompting the user
 * to turn back to portrait.
 *
 * NOTE:
 * - Do NOT use `window.innerHeight` comparisons here.
 *   On iOS, switching keyboard mode (e.g., emoji) changes innerHeight and can
 *   falsely trigger a "landscape" state, stealing focus and collapsing the keyboard.
 */
function PortraitLockWrapper({ children }) {
  const { isMobile } = useIsMobile(768);
  const [isLandscape, setIsLandscape] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Use orientation media query (stable vs iOS keyboard resize)
    const mql = window.matchMedia("(orientation: landscape)");
    const coarsePointerMql = window.matchMedia("(pointer: coarse)");
    const hoverNoneMql = window.matchMedia("(hover: none)");

    const update = () => {
      const navigatorLike = window.navigator || {};
      const ua = String(navigatorLike.userAgent || "");
      const platform = String(navigatorLike.platform || "");
      const maxTouchPoints = Number(navigatorLike.maxTouchPoints || 0);
      const isMobileDevice =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua) ||
        (platform === "MacIntel" && maxTouchPoints > 1) ||
        (coarsePointerMql.matches && hoverNoneMql.matches);

      setIsLandscape(Boolean(isMobileDevice && isMobile && mql.matches));
    };

    // Initial check
    update();

    // Listen to orientation changes via MQL (preferred)
    if (mql.addEventListener) {
      mql.addEventListener("change", update);
      coarsePointerMql.addEventListener("change", update);
      hoverNoneMql.addEventListener("change", update);
    } else {
      // Safari < 14
      mql.addListener(update);
      coarsePointerMql.addListener(update);
      hoverNoneMql.addListener(update);
    }

    // Some environments still benefit from this event
    window.addEventListener("orientationchange", update);

    return () => {
      if (mql.removeEventListener) {
        mql.removeEventListener("change", update);
        coarsePointerMql.removeEventListener("change", update);
        hoverNoneMql.removeEventListener("change", update);
      } else {
        mql.removeListener(update);
        coarsePointerMql.removeListener(update);
        hoverNoneMql.removeListener(update);
      }
      window.removeEventListener("orientationchange", update);
    };
  }, [isMobile]);

  if (isLandscape) {
    return (
      <div className="rotate-notice">
        Please turn your device in portrait orientation.
      </div>
    );
  }

  return <>{children}</>;
}

export default PortraitLockWrapper;
